import { useState, useEffect, useMemo } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Textarea } from "../components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "../components/ui/dialog";
import {
  Briefcase, Search, RefreshCw, Eye, CheckCircle2, XCircle, Upload, FileText,
  Download, Clock, FileCheck2, FileX2, AlertTriangle, Paperclip, User, Hash,
} from "lucide-react";
import { useAuth } from "../App";

const ESTADO_BADGE = {
  Pendiente: { class: "bg-slate-100 text-slate-700 border-slate-300", icon: Clock, label: "Pendiente" },
  Enviado: { class: "bg-blue-50 text-blue-700 border-blue-300", icon: FileText, label: "Enviado" },
  Aprobado: { class: "bg-emerald-50 text-emerald-700 border-emerald-300", icon: FileCheck2, label: "Aprobado" },
  Rechazado: { class: "bg-red-50 text-red-700 border-red-300", icon: FileX2, label: "Rechazado" },
};

const TIPOS_LABEL = {
  libro_diario: "Libro Diario",
  declaracion_iva: "Declaración IVA",
  otros: "Otros",
};


const BandejaDocumentosSoporte = () => {
  const { API, token } = useAuth();
  const headers = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);

  const [loading, setLoading] = useState(false);
  const [data, setData] = useState({ items: [], total: 0, por_estado: {} });
  const [filtroEstado, setFiltroEstado] = useState("");
  const [search, setSearch] = useState("");

  const [detalle, setDetalle] = useState(null);
  const [aprobarOpen, setAprobarOpen] = useState(false);
  const [rechazarOpen, setRechazarOpen] = useState(false);
  const [observacion, setObservacion] = useState("");
  const [motivoRechazo, setMotivoRechazo] = useState("");
  const [tipoUpload, setTipoUpload] = useState("libro_diario");
  const [uploading, setUploading] = useState(false);
  const [busy, setBusy] = useState(false);

  const fetchData = async () => {
    setLoading(true);
    try {
      const params = {};
      if (filtroEstado) params.estado = filtroEstado;
      if (search.trim()) params.search = search.trim();
      const r = await axios.get(`${API}/liquidaciones/documentos-soporte/portal`, { params, headers });
      setData(r.data);
    } catch (err) {
      toast.error("No se pudieron cargar las declaraciones del portal");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filtroEstado]);

  const openDetalle = async (decl) => {
    try {
      const r = await axios.get(`${API}/liquidaciones/documentos-soporte/portal/${decl.id}`, { headers });
      setDetalle(r.data);
    } catch {
      toast.error("No se pudo abrir el detalle");
    }
  };

  const refreshDetalle = async () => {
    if (!detalle) return;
    try {
      const r = await axios.get(`${API}/liquidaciones/documentos-soporte/portal/${detalle.id}`, { headers });
      setDetalle(r.data);
    } catch {/* */}
  };

  const downloadArchivo = async (file_id, filename) => {
    try {
      const r = await axios.get(`${API}/liquidaciones/documentos-soporte/archivo/${file_id}`, {
        headers, responseType: "blob",
      });
      const url = window.URL.createObjectURL(new Blob([r.data]));
      const link = document.createElement("a");
      link.href = url;
      link.download = filename || `archivo_${file_id}`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch {
      toast.error("No se pudo descargar el archivo");
    }
  };

  const onUploadFuncionario = async (file) => {
    if (!file || !detalle) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      await axios.post(
        `${API}/liquidaciones/documentos-soporte/portal/${detalle.id}/upload/${tipoUpload}`,
        fd,
        { headers: { ...headers, "Content-Type": "multipart/form-data" } },
      );
      toast.success("Archivo cargado correctamente");
      await refreshDetalle();
      await fetchData();
    } catch (err) {
      const msg = err.response?.data?.detail || "Error al cargar el archivo";
      toast.error(msg);
    } finally {
      setUploading(false);
    }
  };

  const onAprobar = async () => {
    if (!detalle) return;
    setBusy(true);
    try {
      const r = await axios.post(
        `${API}/liquidaciones/documentos-soporte/portal/${detalle.id}/aprobar`,
        { observacion: observacion.trim() || null },
        { headers },
      );
      toast.success(
        `Declaración aprobada. Planilla generada: ${r.data.nro_planilla}`,
      );
      setAprobarOpen(false);
      setObservacion("");
      setDetalle(null);
      await fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al aprobar");
    } finally {
      setBusy(false);
    }
  };

  const onRechazar = async () => {
    if (!detalle) return;
    if (!motivoRechazo.trim()) {
      toast.error("El motivo es obligatorio");
      return;
    }
    setBusy(true);
    try {
      await axios.post(
        `${API}/liquidaciones/documentos-soporte/portal/${detalle.id}/rechazar`,
        { motivo: motivoRechazo.trim() },
        { headers },
      );
      toast.success("Declaración rechazada");
      setRechazarOpen(false);
      setMotivoRechazo("");
      setDetalle(null);
      await fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al rechazar");
    } finally {
      setBusy(false);
    }
  };

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return data.items;
    return data.items.filter((it) =>
      [it.nro_planilla, it.contribuyente_rif, it.contribuyente_nombre]
        .some((v) => (v || "").toString().toLowerCase().includes(q))
    );
  }, [data.items, search]);

  const counts = data.por_estado || {};

  return (
    <div className="space-y-6" data-testid="bandeja-documentos-soporte">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 bg-purple-100 rounded-xl flex items-center justify-center">
            <Briefcase className="w-6 h-6 text-purple-600" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-slate-900" style={{ fontFamily: "Manrope, sans-serif" }}>
              Cargar Documentos Soporte
            </h1>
            <p className="text-slate-500 text-sm">
              Bandeja de declaraciones generadas por contribuyentes en el portal. Aprueba, rechaza o adjunta archivos.
            </p>
          </div>
        </div>
        <Button variant="outline" onClick={fetchData} disabled={loading} className="gap-2" data-testid="btn-refresh">
          <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} /> Actualizar
        </Button>
      </div>

      {/* KPIs por estado */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {["Pendiente", "Enviado", "Aprobado", "Rechazado"].map((e) => {
          const cfg = ESTADO_BADGE[e];
          const Icon = cfg.icon;
          return (
            <Card
              key={e}
              className={`cursor-pointer border-l-4 transition ${filtroEstado === e ? "ring-2 ring-blue-500" : ""}`}
              style={{ borderLeftColor: e === "Pendiente" ? "#94a3b8" : e === "Enviado" ? "#3b82f6" : e === "Aprobado" ? "#10b981" : "#ef4444" }}
              onClick={() => setFiltroEstado(filtroEstado === e ? "" : e)}
              data-testid={`kpi-${e.toLowerCase()}`}
            >
              <CardContent className="pt-4 pb-4 flex items-center justify-between">
                <div>
                  <p className="text-[10px] uppercase tracking-wider text-slate-500 font-medium">{e}</p>
                  <p className="text-2xl font-bold text-slate-900 mt-1">{counts[e] || 0}</p>
                </div>
                <Icon className="w-7 h-7 text-slate-400" />
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Filtros */}
      <Card>
        <CardContent className="pt-4 pb-4 flex items-end gap-3 flex-wrap">
          <div className="flex-1 min-w-[260px]">
            <Label className="text-xs text-slate-500 mb-1 block">Buscar (RIF, nombre, planilla)</Label>
            <div className="relative">
              <Search className="absolute left-2 top-2.5 w-4 h-4 text-slate-400" />
              <Input
                className="pl-8"
                placeholder="Buscar..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && fetchData()}
                data-testid="input-search"
              />
            </div>
          </div>
          <div>
            <Label className="text-xs text-slate-500 mb-1 block">Estado</Label>
            <Select value={filtroEstado || "all"} onValueChange={(v) => setFiltroEstado(v === "all" ? "" : v)}>
              <SelectTrigger className="w-[170px]" data-testid="select-estado">
                <SelectValue placeholder="Todos" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos</SelectItem>
                <SelectItem value="Pendiente">Pendiente</SelectItem>
                <SelectItem value="Enviado">Enviado</SelectItem>
                <SelectItem value="Aprobado">Aprobado</SelectItem>
                <SelectItem value="Rechazado">Rechazado</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <Button variant="outline" onClick={() => { setFiltroEstado(""); setSearch(""); }} data-testid="btn-reset">
            Limpiar
          </Button>
        </CardContent>
      </Card>

      {/* Tabla */}
      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="flex justify-center py-12">
              <RefreshCw className="w-8 h-8 animate-spin text-blue-600" />
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b bg-slate-50 text-slate-600 text-xs uppercase tracking-wide">
                    <th className="px-3 py-3 text-left">Nro Planilla</th>
                    <th className="px-3 py-3 text-left">RIF</th>
                    <th className="px-3 py-3 text-left">Contribuyente</th>
                    <th className="px-3 py-3 text-left">Clase</th>
                    <th className="px-3 py-3 text-left">Fecha</th>
                    <th className="px-3 py-3 text-center">Archivos</th>
                    <th className="px-3 py-3 text-center">Estado</th>
                    <th className="px-3 py-3 text-right">Acción</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.length === 0 ? (
                    <tr>
                      <td colSpan={8} className="px-4 py-12 text-center text-slate-400">
                        Sin declaraciones que coincidan con los filtros
                      </td>
                    </tr>
                  ) : filtered.map((it) => {
                    const badge = ESTADO_BADGE[it.estatus_aprobacion] || ESTADO_BADGE.Pendiente;
                    return (
                      <tr key={it.id} className="border-b hover:bg-slate-50/60" data-testid={`row-${it.id}`}>
                        <td className="px-3 py-2 font-mono font-semibold text-purple-700">{it.nro_planilla}</td>
                        <td className="px-3 py-2 font-mono text-slate-700">{it.contribuyente_rif || "—"}</td>
                        <td className="px-3 py-2 text-slate-800">{it.contribuyente_nombre || "—"}</td>
                        <td className="px-3 py-2 text-slate-500 text-xs">{it.clase_contribuyente || "—"}</td>
                        <td className="px-3 py-2 text-slate-600 text-xs">{it.fecha}</td>
                        <td className="px-3 py-2 text-center">
                          <Badge variant="outline" className={it.total_archivos > 0 ? "bg-blue-50 text-blue-700" : "bg-slate-50 text-slate-400"}>
                            <Paperclip className="w-3 h-3 mr-1" /> {it.total_archivos}
                          </Badge>
                        </td>
                        <td className="px-3 py-2 text-center">
                          <Badge variant="outline" className={badge.class}>{badge.label}</Badge>
                        </td>
                        <td className="px-3 py-2 text-right">
                          <Button size="sm" variant="outline" onClick={() => openDetalle(it)} data-testid={`btn-ver-${it.id}`}>
                            <Eye className="w-3.5 h-3.5 mr-1" /> Ver
                          </Button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Detalle Dialog */}
      <Dialog open={!!detalle} onOpenChange={(o) => !o && setDetalle(null)}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="dialog-detalle">
          {detalle && (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  <FileText className="w-5 h-5 text-purple-600" />
                  Declaración {detalle.nro_planilla}
                </DialogTitle>
                <DialogDescription>
                  Gestione los documentos de soporte y apruebe o rechace esta declaración.
                </DialogDescription>
              </DialogHeader>

              {/* Información */}
              <div className="grid grid-cols-2 gap-3 text-sm bg-slate-50 p-4 rounded-lg">
                <div>
                  <Label className="text-[10px] uppercase text-slate-500">RIF</Label>
                  <p className="font-mono font-semibold text-slate-800">{detalle.contribuyente_rif || "—"}</p>
                </div>
                <div>
                  <Label className="text-[10px] uppercase text-slate-500">Contribuyente</Label>
                  <p className="text-slate-800">{detalle.contribuyente_nombre || "—"}</p>
                </div>
                <div>
                  <Label className="text-[10px] uppercase text-slate-500">Clase</Label>
                  <p className="text-slate-700">{detalle.clase_contribuyente || "—"}</p>
                </div>
                <div>
                  <Label className="text-[10px] uppercase text-slate-500">Estado</Label>
                  <Badge variant="outline" className={(ESTADO_BADGE[detalle.estatus_aprobacion] || ESTADO_BADGE.Pendiente).class}>
                    {detalle.estatus_aprobacion}
                  </Badge>
                </div>
                <div>
                  <Label className="text-[10px] uppercase text-slate-500">Fecha creación</Label>
                  <p className="text-slate-700 text-xs">{detalle.fecha}</p>
                </div>
                {detalle.fecha_envio && (
                  <div>
                    <Label className="text-[10px] uppercase text-slate-500">Enviado</Label>
                    <p className="text-slate-700 text-xs">{detalle.fecha_envio.slice(0, 19).replace("T", " ")}</p>
                  </div>
                )}
                {detalle.aprobado_at && (
                  <div className="col-span-2">
                    <Label className="text-[10px] uppercase text-emerald-700">Aprobado por</Label>
                    <p className="text-emerald-800 text-xs">
                      {detalle.aprobado_por} · {detalle.aprobado_at.slice(0, 19).replace("T", " ")}
                      {detalle.datos_declaracion_id && (
                        <span className="ml-2 text-emerald-600">→ Replicado a datos_declaracion</span>
                      )}
                    </p>
                  </div>
                )}
                {detalle.motivo_rechazo && (
                  <div className="col-span-2 bg-red-50 p-2 rounded border border-red-200">
                    <Label className="text-[10px] uppercase text-red-700">Motivo de rechazo</Label>
                    <p className="text-red-800 text-xs">{detalle.motivo_rechazo}</p>
                  </div>
                )}
              </div>

              {/* Archivos */}
              <div className="space-y-2">
                <Label className="text-sm font-semibold text-slate-700">Documentos Adjuntos ({detalle.total_archivos})</Label>
                {detalle.archivos.length === 0 ? (
                  <div className="text-center py-6 text-slate-400 text-sm bg-slate-50 rounded">
                    <AlertTriangle className="w-6 h-6 mx-auto mb-2 text-amber-500" />
                    Sin archivos adjuntos todavía
                  </div>
                ) : (
                  <div className="space-y-2">
                    {detalle.archivos.map((a, idx) => (
                      <div key={idx} className="flex items-center justify-between p-3 border rounded-lg bg-white hover:bg-slate-50/60"
                           data-testid={`archivo-${a.file_id}`}>
                        <div className="flex items-center gap-3 min-w-0">
                          <FileText className="w-5 h-5 text-blue-600 flex-shrink-0" />
                          <div className="min-w-0">
                            <p className="text-sm font-medium text-slate-800 truncate">{a.original_filename}</p>
                            <p className="text-[10px] text-slate-500">
                              {TIPOS_LABEL[a.tipo_archivo] || a.tipo_archivo} ·
                              {" "}{((a.size || 0) / 1024).toFixed(1)} KB ·
                              {" "}<span className={a.uploaded_by_role === "funcionario" ? "text-purple-600" : "text-blue-600"}>
                                {a.uploaded_by_role === "funcionario" ? "Funcionario" : "Contribuyente"}
                              </span>
                            </p>
                          </div>
                        </div>
                        <Button size="sm" variant="outline" onClick={() => downloadArchivo(a.file_id, a.original_filename)}
                                data-testid={`btn-download-${a.file_id}`}>
                          <Download className="w-3.5 h-3.5 mr-1" /> Descargar
                        </Button>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Adjuntar archivo funcionario */}
              {detalle.estatus_aprobacion !== "Aprobado" && (
                <div className="border-t pt-3 space-y-2">
                  <Label className="text-sm font-semibold text-slate-700">Adjuntar documento (funcionario)</Label>
                  <div className="flex items-center gap-2">
                    <Select value={tipoUpload} onValueChange={setTipoUpload}>
                      <SelectTrigger className="w-[180px]" data-testid="select-tipo-upload">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="libro_diario">Libro Diario</SelectItem>
                        <SelectItem value="declaracion_iva">Declaración IVA</SelectItem>
                        <SelectItem value="otros">Otros</SelectItem>
                      </SelectContent>
                    </Select>
                    <Input
                      type="file"
                      accept=".pdf,.jpg,.jpeg,.png"
                      onChange={(e) => onUploadFuncionario(e.target.files?.[0])}
                      disabled={uploading}
                      data-testid="input-file-upload"
                    />
                  </div>
                  <p className="text-[10px] text-slate-500">PDF/JPG/PNG · Máx 10MB · El archivo del mismo tipo será reemplazado</p>
                </div>
              )}

              <DialogFooter className="gap-2 flex-wrap">
                <Button variant="outline" onClick={() => setDetalle(null)}>Cerrar</Button>
                {detalle.estatus_aprobacion !== "Aprobado" && detalle.estatus_aprobacion !== "Rechazado" && (
                  <>
                    <Button
                      variant="outline"
                      className="border-red-300 text-red-700 hover:bg-red-50"
                      onClick={() => setRechazarOpen(true)}
                      data-testid="btn-rechazar"
                    >
                      <XCircle className="w-4 h-4 mr-2" /> Rechazar
                    </Button>
                    <Button
                      className="bg-emerald-600 hover:bg-emerald-700 text-white"
                      onClick={() => setAprobarOpen(true)}
                      disabled={detalle.total_archivos === 0}
                      data-testid="btn-aprobar"
                    >
                      <CheckCircle2 className="w-4 h-4 mr-2" /> Aprobar y replicar
                    </Button>
                  </>
                )}
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Aprobar Dialog */}
      <Dialog open={aprobarOpen} onOpenChange={setAprobarOpen}>
        <DialogContent data-testid="dialog-aprobar">
          <DialogHeader>
            <DialogTitle>Aprobar declaración</DialogTitle>
            <DialogDescription>
              Se replicará a <strong>datos_declaracion</strong> con un nuevo Nro Planilla y entrará al flujo normal de liquidaciones.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <Label>Observación (opcional)</Label>
            <Textarea value={observacion} onChange={(e) => setObservacion(e.target.value)} rows={3}
                      placeholder="Comentario interno..." data-testid="input-observacion" />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAprobarOpen(false)}>Cancelar</Button>
            <Button className="bg-emerald-600 text-white" onClick={onAprobar} disabled={busy} data-testid="btn-confirm-aprobar">
              {busy ? "Aprobando..." : "Confirmar aprobación"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Rechazar Dialog */}
      <Dialog open={rechazarOpen} onOpenChange={setRechazarOpen}>
        <DialogContent data-testid="dialog-rechazar">
          <DialogHeader>
            <DialogTitle>Rechazar declaración</DialogTitle>
            <DialogDescription>
              El contribuyente verá el motivo del rechazo en su portal y podrá corregir.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <Label>Motivo del rechazo <span className="text-red-600">*</span></Label>
            <Textarea value={motivoRechazo} onChange={(e) => setMotivoRechazo(e.target.value)} rows={4}
                      placeholder="Explique por qué se rechaza..." data-testid="input-motivo-rechazo" />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRechazarOpen(false)}>Cancelar</Button>
            <Button className="bg-red-600 text-white" onClick={onRechazar} disabled={busy} data-testid="btn-confirm-rechazar">
              {busy ? "Rechazando..." : "Confirmar rechazo"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};


export default BandejaDocumentosSoporte;
